/**
 * CANADA-SCOPE RENDER GUARD (founder finding 2026-07-06 §5.1 — "Canada-scoped default COP").
 *
 * The default COP is a CANADIAN common operating picture, so the map must never RENDER a
 * hazard marker outside Canada — no matter what lands inside a fetched bbox. Two ways junk
 * gets there, both proven live:
 *   • a continent-zoom camera rectangle (rectangleToBbox) spans the Pacific/Atlantic oceans
 *     + the US interior, so the per-layer bbox fetch legitimately returns anything there;
 *   • the SHARED dev Postgres carries ocean-coordinate TEST FIXTURES (the "Atlantic dead-zone"
 *     at ≈(-30,20) / (-54.99,-54.99) that sibling RUN_DB_TESTS suites insert), which the
 *     fusion engine turns into LIVE high_risk_hotspots rows that render for up to 6h.
 * Neither the endpoints nor the RL3/RL4 map dots (no country-zoom distance gate) filter these
 * geographically, so the ONLY thing keeping ocean/US dots off the national COP was data
 * cleanliness — which the founder's 2026-07-06 EVE spot-check caught failing. This guard makes
 * the map render Canada-scoped structurally instead.
 *
 * Bounds MIRROR api.CANADA_BBOX — the same envelope the Anomaly Queue's "▲ N outside view —
 * Show all Canada" affordance already uses (that affordance is UNCHANGED: this only stops the
 * MAP rendering markers the queue was already treating as "outside view"). Kept in lockstep by
 * canadaScope.test.ts. Self-contained (own literal, no import) so a layer test that stubs the
 * api module never leaves the guard undefined.
 */
export const CANADA_SCOPE_BBOX = {
  west: -141.5,
  south: 41.0,
  east: -52.0,
  north: 83.5,
} as const;

/**
 * True when a marker at (lon, lat) is inside the Canada scope envelope — the render gate every
 * hazard-marker layer applies before drawing a dot/disc. A non-finite coordinate (a NaN from a
 * degenerate geometry / null → 0 bug) is treated as OUT of scope (never rendered), so a bad
 * coordinate can't slip a marker onto the globe at (0,0) either.
 */
export function isWithinCanadaScope(lon: number, lat: number): boolean {
  return (
    Number.isFinite(lon) &&
    Number.isFinite(lat) &&
    lon >= CANADA_SCOPE_BBOX.west &&
    lon <= CANADA_SCOPE_BBOX.east &&
    lat >= CANADA_SCOPE_BBOX.south &&
    lat <= CANADA_SCOPE_BBOX.north
  );
}
